import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import {
  ArrowLeft,
  Menu,
  Building2,
  Users,
  CreditCard,
  Crown,
  Pencil,
  Check,
  X,
  ChevronRight,
  Plus,
  AlertTriangle,
} from 'lucide-react';
import { useAuth, usePermissions } from '@/contexts/AuthContext';
import { useChatLayout } from '@/components/layout/ChatLayout';
import WorkspacePill from '@/components/layout/WorkspacePill';
import { teamApi } from '@/services/team-api';
import { workspaceApi } from '@/services/workspace-api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import NameModal from '@/components/ui/NameModal';

const STATUS_LABELS: Record<string, string> = {
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Past due',
  canceled: 'Canceled',
};

const STATUS_COLORS: Record<string, string> = {
  trialing: 'bg-info/15 text-info',
  active: 'bg-success/15 text-success',
  past_due: 'bg-warn/15 text-warn',
  canceled: 'bg-destructive/15 text-destructive',
};

const WorkspacePage: React.FC = () => {
  const { workspace, memberships, refetch, switchWorkspace } = useAuth();
  const { canManageOrganization, isOwner } = usePermissions();
  const { isMobile, toggleSidebar } = useChatLayout();
  const queryClient = useQueryClient();
  const isAdmin = canManageOrganization();

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  // Members + pooled seats. Available to any member (read-only for non-admins).
  const usersQuery = useQuery({
    queryKey: ['orgUsers'],
    queryFn: teamApi.listUsers,
  });

  const renameMutation = useMutation({
    mutationFn: (name: string) => workspaceApi.rename(name),
    onSuccess: () => {
      toast.success('Workspace renamed');
      setEditingName(false);
      refetch(); // repoints the switcher / pill labels
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });

  if (!workspace) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const activeMembership = memberships.find((m) => m.workspace.id === workspace.id);
  const businessCount = activeMembership?.organizations.length ?? 0;
  const memberCount = usersQuery.data?.total ?? usersQuery.data?.users.length ?? 0;
  const seatLimit = usersQuery.data?.seatLimit ?? 0;
  const seatsUsed = usersQuery.data?.seatsUsed ?? 0;

  const startEdit = () => {
    setNameDraft(workspace.name);
    setEditingName(true);
  };

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button onClick={toggleSidebar} className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors" aria-label="Open sidebar">
          <Menu className="w-5 h-5" />
        </button>
      )}

      <div className="w-full max-w-4xl mx-auto space-y-6">
        {/* Header — back link on the left, workspace selector top-right (matches /home) */}
        <div className="flex items-center justify-between gap-4">
          <Link to="/settings" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Settings
          </Link>
          <WorkspacePill />
        </div>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <h1 className="text-3xl font-bold text-foreground">Workspace</h1>
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-[10px] border border-input bg-card px-3 py-1.5 text-[13px] font-medium text-foreground hover:bg-accent"
          >
            <Plus className="w-3.5 h-3.5" /> New workspace
          </button>
        </div>

        {/* Identity card */}
        <div className="rounded-xl bg-muted border border-border p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center text-lg font-bold flex-shrink-0">
              {monogram(workspace.name)}
            </div>
            <div className="min-w-0 flex-1">
              <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                Workspace name
              </label>
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    autoFocus
                    className="max-w-xs"
                  />
                  <Button
                    size="sm"
                    onClick={() => renameMutation.mutate(nameDraft.trim())}
                    isLoading={renameMutation.isPending}
                    disabled={!nameDraft.trim() || nameDraft.trim() === workspace.name}
                    leftIcon={<Check className="w-4 h-4" />}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingName(false)}
                    disabled={renameMutation.isPending}
                    leftIcon={<X className="w-4 h-4" />}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-lg font-semibold text-foreground truncate">{workspace.name}</p>
                  {isAdmin && (
                    <button
                      onClick={startEdit}
                      className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                      aria-label="Rename workspace"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 mt-3">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent text-primary capitalize">
                  {workspace.subscriptionTier} plan
                </span>
                <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', STATUS_COLORS[workspace.subscriptionStatus] ?? 'bg-muted text-muted-foreground')}>
                  {STATUS_LABELS[workspace.subscriptionStatus] ?? workspace.subscriptionStatus}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Overview stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            icon={<Building2 className="w-5 h-5" />}
            value={businessCount}
            label={businessCount === 1 ? 'Business' : 'Businesses'}
            to="/businesses"
            linkText="Manage"
          />
          <StatCard
            icon={<Users className="w-5 h-5" />}
            value={usersQuery.isLoading ? '—' : memberCount}
            label={memberCount === 1 ? 'Member' : 'Members'}
            to="/settings/team"
            linkText="Manage members"
          />
          <StatCard
            icon={<CreditCard className="w-5 h-5" />}
            value={seatLimit > 0 ? `${seatsUsed}/${seatLimit}` : (usersQuery.isLoading ? '—' : String(seatsUsed))}
            label="Seats used"
            to="/settings/billing"
            linkText="Billing & plan"
          />
        </div>

        {/* Ownership (owner only) — transfer lives in Team members (kept separate). */}
        {isOwner && (
          <div className="rounded-xl bg-muted border border-border p-6">
            <div className="flex items-start gap-3">
              <Crown className="w-5 h-5 text-warn flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-sm font-semibold text-foreground">You own this workspace</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Ownership carries billing and full administration. To hand it to a teammate, use
                  Transfer ownership from{' '}
                  <Link to="/settings/team" className="font-medium text-primary hover:underline">
                    Team members
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Danger zone (owner only) — closing the whole workspace lives on the
            account-closure page; this is the entry point to it. */}
        {isOwner && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/[0.04] p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-sm font-semibold text-foreground">Close this workspace</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Permanently delete <strong className="text-foreground">{workspace.name}</strong> and every
                    business, source, and record inside it. This can&apos;t be undone.
                  </p>
                </div>
              </div>
              <Link
                to="/settings/account-closure"
                className="inline-flex items-center gap-1.5 rounded-[10px] border border-destructive/40 bg-card px-3 py-1.5 text-[13px] font-medium text-destructive hover:bg-destructive/10 whitespace-nowrap"
              >
                Close workspace…
              </Link>
            </div>
          </div>
        )}
      </div>

      {createOpen && (
        <NameModal
          title="New workspace"
          submitText="Create"
          label="Workspace name"
          placeholder="e.g. Northside Holdings"
          onClose={() => setCreateOpen(false)}
          onSubmit={async (name) => {
            const { workspace: created } = await workspaceApi.create(name);
            toast.success('Workspace created');
            await switchWorkspace(created.id); // repoints active context → pill becomes a dropdown
          }}
        />
      )}
    </div>
  );
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  value: React.ReactNode;
  label: string;
  to: string;
  linkText: string;
}> = ({ icon, value, label, to, linkText }) => (
  <div className="rounded-xl bg-muted border border-border p-5 flex flex-col">
    <div className="flex items-center gap-2 text-muted-foreground">
      {icon}
      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
    </div>
    <p className="text-3xl font-bold text-foreground tabular-nums mt-2">{value}</p>
    <Link to={to} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
      {linkText} <ChevronRight className="w-4 h-4" />
    </Link>
  </div>
);

function monogram(name: string): string {
  const words = name.trim().split(/\s+/);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '?';
}

export default WorkspacePage;
